// `@bindery/speech/contract` — the zod schemas every half of the package and
// every consumer agree on: what the browser captured, what it sends, what the
// Worker answers, how either side fails, and the record a scorer may read.
// Compiled by both tsconfigs, so it may use neither runtime's globals.
//
// Each schema is paired with a type of the same name (`z.infer`). Errors are
// values (functional-programming POLICY.md §8): every fallible operation in
// the package returns one of the tag-discriminated unions below, and nothing
// throws across the public surface. `Result<T>` in
// packages/shared/src/std/try-catch.ts fixes its error type to `Error`, which
// cannot carry this taxonomy, so it is not used here.
//
// docs/plan/speech-transcription-package.md § "Types and data shapes" is the
// first draft of this file; where the two disagree, this file wins.
import * as z from "zod";
import { ReaderIdSchema } from "@bindery/shared/auth/readerId";
import { assertNever } from "@bindery/shared/std/assert-never";

// ---------------------------------------------------------------------------
// Identifiers
// ---------------------------------------------------------------------------

// A reader's pseudonymous id, under the same rule as the broker's session
// contract: the route takes it from a verified session.
export const ReaderId = ReaderIdSchema;
export type ReaderId = z.infer<typeof ReaderId>;

// Minted by the Worker per transcription. Stays on our side of the provider.
export const ResponseId = z.uuid();
export type ResponseId = z.infer<typeof ResponseId>;

// Opaque reference to the item being answered; never sent to the provider.
export const ItemRef = z.object({
  kind: z.enum([
    "comprehension-question",
    "retelling",
    "free-response",
    "composition",
  ]),
  itemId: z.string().min(1).max(128),
  answerKeyVersion: z.string().max(64).optional(),
});
export type ItemRef = z.infer<typeof ItemRef>;

// Whether the route's cleanup judge may see the question the child was asked
// (Decision 6). The rule is whether the question has a right or wrong content
// answer: where it does, a judge that knows the question leans toward edits
// that make the answer more correct (the prompt-biasing hazard of Open
// question 7). A Record over every kind, so a new kind does not compile until
// someone decides.
export const JUDGE_SEES_PROMPT: Readonly<Record<ItemRef["kind"], boolean>> = {
  // Has a right answer: the question would pull the judge toward it.
  "comprehension-question": false,
  // Scored against the passage: the question would let the judge fill in what was left out.
  retelling: false,
  // Nothing to get right ("Describe your favorite book."): the question helps recognize the child's words.
  "free-response": true,
  // The child's own story: nothing to get right, so the free-response rule.
  // Moot while compositions run no cleanup (raw only), but decided so a
  // cleanup turned on later does not inherit a choice nobody made.
  composition: true,
};

// Which try at one item this is. The ceiling is a sanity bound on the wire;
// the attempt cap a child actually meets is the host's quota (`QuotaOutcome`).
export const Attempt = z.number().int().min(1).max(10);
export type Attempt = z.infer<typeof Attempt>;

// ---------------------------------------------------------------------------
// Audio and capture
// ---------------------------------------------------------------------------

// The containers the capture side may produce and the server accepts.
export const AudioContainer = z.enum(["webm", "mp4", "ogg", "wav"]);
export type AudioContainer = z.infer<typeof AudioContainer>;

// What MediaRecorder reported, verbatim (e.g. "audio/webm;codecs=opus"). The
// codec parameter is informational: the server keys off the sniffed container.
export const AudioMime = z.string().regex(/^audio\/(webm|mp4|ogg|wav)(;.*)?$/);
export type AudioMime = z.infer<typeof AudioMime>;

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
//
// One set of numbers for the browser, the transcribe route and the
// speech_tokenizer Worker (apps/speech-tokenizer), which all import these.

// About two minutes: the longest spoken answer accepted.
export const MAX_AUDIO_SECONDS = 120;
// The worst accepted encoding per second: 16-bit 48 kHz mono PCM WAV. The
// server cannot measure duration without decoding, so bytes stand in for it.
export const MAX_AUDIO_BYTES_PER_SECOND = 96_000;
// Two minutes of the worst encoding: 11,520,000 bytes.
export const MAX_AUDIO_BYTES = MAX_AUDIO_SECONDS * MAX_AUDIO_BYTES_PER_SECOND;
// The longest question prompt, in whitespace-separated words after trimming.
export const MAX_PROMPT_WORDS = 500;
// A cheap guard on the prompt's characters, checked before splitting words.
export const MAX_PROMPT_CHARS = 4000;

// The composition so far, sent as Whisper `prompt` context so the child's
// own words are recognized again. Whisper keeps only the final 224 tokens of
// a prompt, so more than this buys nothing but a bigger form.
export const MAX_TRANSCRIPTION_CONTEXT_CHARS = 1000;
export const MAX_TRANSCRIPTION_CONTEXT_WORDS = 200;

export const CaptureLimits = z.object({
  maxDurationMs: z.number().int().positive(), // hard stop
  warnAtMs: z.number().int().positive(), // "almost done" cue
  minDurationMs: z.number().int().nonnegative(), // below this is "too short", never uploaded
  maxBytes: z.number().int().positive(), // client and server both enforce
});
export type CaptureLimits = z.infer<typeof CaptureLimits>;

export const DEFAULT_CAPTURE_LIMITS: CaptureLimits = {
  maxDurationMs: MAX_AUDIO_SECONDS * 1000,
  warnAtMs: MAX_AUDIO_SECONDS * 1000 - 10_000,
  minDurationMs: 1_000,
  maxBytes: MAX_AUDIO_BYTES,
};

// Words as the Python service counts them for its cap: `str.split()` on the
// trimmed text.
export const promptWordCount = (text: string): number =>
  text.split(/\s+/).filter((w) => w.length > 0).length;

export const CaptureMeta = z.object({
  mime: AudioMime, // recorder.mimeType after start(), not the requested type
  durationMs: z.number().int().nonnegative(), // wall-clock start→stop, measured by the driver
  bytes: z.number().int().nonnegative(),
  endedBy: z.enum(["user", "max-duration", "track-ended"]),
});
export type CaptureMeta = z.infer<typeof CaptureMeta>;
// In the browser a Recording is { meta: CaptureMeta; blob: Blob } — the Blob
// is never parsed by zod, only its meta — so it is declared in ./capture,
// where `Blob` means the DOM's.

// ---------------------------------------------------------------------------
// Transcription request (browser → Worker)
// ---------------------------------------------------------------------------

// Form fields other than the audio part, parsed on the Worker from FormData.
// `meta` arrives JSON-encoded; decoding it is the route's job. There is no
// `readerId` field: who is speaking comes from `authorize`, because a
// client-supplied reader id would be a spoofable claim on a minor's record.
export const TranscriptionRequestFields = z
  .object({
    // What the child was asked. Sent to the provider as ASR context.
    promptText: z
      .string()
      .max(MAX_PROMPT_CHARS)
      .refine((t) => promptWordCount(t) <= MAX_PROMPT_WORDS, {
        message: `at most ${MAX_PROMPT_WORDS} words`,
      }),
    itemRef: ItemRef,
    language: z
      .string()
      .regex(/^[a-z]{2,3}(-[A-Z]{2})?$/)
      .default("en-US"),
    meta: CaptureMeta,
    attempt: Attempt,
    // Composition only: the story so far and the child's confirmed words, as
    // ASR context. Refused for every other kind, whose prompt it would change.
    transcriptionContext: z
      .string()
      .max(MAX_TRANSCRIPTION_CONTEXT_CHARS)
      .refine((t) => promptWordCount(t) <= MAX_TRANSCRIPTION_CONTEXT_WORDS, {
        message: `at most ${MAX_TRANSCRIPTION_CONTEXT_WORDS} words`,
      })
      .optional(),
  })
  .refine(
    (f) =>
      f.transcriptionContext === undefined || f.itemRef.kind === "composition",
    { path: ["transcriptionContext"], message: "composition items only" },
  );
export type TranscriptionRequestFields = z.infer<
  typeof TranscriptionRequestFields
>;

// The multipart part names: the audio part plus one per request field. Here
// rather than in either half because the client writes them and the route
// reads them, and neither half may import the other. `itemRef` and `meta`
// travel JSON-encoded; `attempt` as its decimal string.
export const FORM_FIELDS = {
  audio: "audio",
  promptText: "promptText",
  itemRef: "itemRef",
  language: "language",
  meta: "meta",
  attempt: "attempt",
  transcriptionContext: "transcriptionContext",
} as const satisfies Readonly<
  Record<"audio" | keyof TranscriptionRequestFields, string>
>;

// ---------------------------------------------------------------------------
// Provider-level result (adapter → route)
// ---------------------------------------------------------------------------

export const TranscriptWord = z.object({
  text: z.string(),
  startMs: z.number().nonnegative(),
  endMs: z.number().nonnegative(),
  // A per-word probability, only from a provider that reports one; whisper-1
  // does not.
  confidence: z.number().min(0).max(1).optional(),
  // The average token log-probability of the ASR segment holding this word
  // (Whisper's `avg_logprob`): a segment-level signal repeated per word, not
  // a confidence in this word.
  segmentLogprob: z.number().max(0).optional(),
});
export type TranscriptWord = z.infer<typeof TranscriptWord>;

// A probability in [0, 1].
const Probability = z.number().min(0).max(1);

// The route's cleanup judge on one cleanup (Decision 6): decomposed questions
// matching the cleanup prompt's rules, each a probability, plus whether the
// cleaned text still says what the child said. No verdict: accept/review
// thresholds are the host's configuration, and a changed threshold must not
// rewrite a stored record.
export const CleanupJudgement = z.object({
  judge: z.string().min(1), // e.g. "jev"
  version: z.string().min(1), // pins the judge, as Provenance pins the models
  contentAdded: Probability,
  contentRemoved: Probability,
  wordChoiceChanged: Probability,
  sentenceRestructured: Probability,
  grammarCorrected: Probability,
  pronunciationSpellingFixed: Probability,
  preservesMeaning: Probability,
  // Whether the judge was shown the question (JUDGE_SEES_PROMPT).
  sawPrompt: z.boolean(),
});
export type CleanupJudgement = z.infer<typeof CleanupJudgement>;

// The LLM cleanup pass over the raw ASR text. `judgement` is null when no
// judge is configured or it failed: the cleaned text is returned, but nothing
// claims it was accepted.
export const TranscriptCleanup = z.object({
  text: z.string(),
  judgement: CleanupJudgement.nullable(),
});
export type TranscriptCleanup = z.infer<typeof TranscriptCleanup>;

// A flagged-list word's place in `Transcript.raw`: UTF-16 offsets, `start`
// inclusive and `end` exclusive, so `raw.slice(start, end)` is the word.
export const FlaggedSpan = z
  .object({
    start: z.number().int().nonnegative(),
    end: z.number().int().positive(),
  })
  .refine((s) => s.end > s.start, { message: "end after start" });
export type FlaggedSpan = z.infer<typeof FlaggedSpan>;

// Carries both texts and never one ambiguous "text": the scorer or the
// research design chooses which to score (Decision 2).
export const Transcript = z.object({
  // Verbatim ASR output. Must contain a non-space character: an empty
  // transcript is the `no-speech` error, never an `ok` result, because a
  // scorer would mark an empty answer wrong rather than missing.
  raw: z.string().regex(/\S/),
  // Null when no cleanup ran — disabled, or the cleanup call failed.
  cleanup: TranscriptCleanup.nullable(),
  // A flagged-word list matched. Flag, never reject (Decision 3): the child
  // sees nothing different, the aide surface can review. Which word matched
  // is content, and is not carried.
  flagged: z.boolean(),
  // Composition only: where in `raw` each flagged word is, so the child's
  // screen can mask it. Positions within text the response already carries,
  // so no new content; like the text, they never reach a log.
  flaggedSpans: z.array(FlaggedSpan).optional(),
  words: z.array(TranscriptWord).optional(), // present iff the provider offers timestamps
  language: z.string().optional(), // detected, if reported
  // Provider-reported signals of ASR quality, normalized to [0, 1] where the
  // provider offers them. Absent means "unknown", never "perfect".
  quality: z
    .object({
      meanWordConfidence: z.number().min(0).max(1).optional(),
      noSpeechProbability: z.number().min(0).max(1).optional(),
      // Whisper's segment `avg_logprob`, weighted by segment duration. A
      // log-probability, not normalized. Whisper reportedly treats below -1
      // as a failed decode (unverified, check before relying on it).
      meanSegmentLogprob: z.number().max(0).optional(),
    })
    .default({}),
});
export type Transcript = z.infer<typeof Transcript>;

export const Provenance = z.object({
  provider: z.enum(["speech-tokenizer", "openai-direct", "self-hosted"]),
  // Whatever pins each model: a model id, a deployment version, a git sha.
  // Required, because a silent model change shifts a research outcome. Two
  // models determine the text, so both are named; `cleanup` is null when the
  // deployment runs no cleanup pass at all.
  models: z.object({
    asr: z.string().min(1),
    cleanup: z.string().min(1).nullable(),
  }),
  receivedAt: z.number().int(), // epoch ms, server clock
});
export type Provenance = z.infer<typeof Provenance>;

// ---------------------------------------------------------------------------
// Error taxonomy
// ---------------------------------------------------------------------------
//
// One closed union per side. Every `kind` is unique across the two unions,
// so `childFacing` and a log mapping can switch on `kind` alone over their
// union and each case narrows to exactly one shape.

// Browser-side capture failures. Mapped from DOMException names in one
// function in ./capture.
export const CaptureError = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("unsupported"),
    reason: z.enum([
      "no-media-devices",
      "no-media-recorder",
      "no-supported-mime",
      "insecure-context",
    ]),
  }),
  z.object({ kind: z.literal("permission-denied") }), // NotAllowedError / SecurityError
  z.object({ kind: z.literal("no-microphone") }), // NotFoundError / OverconstrainedError
  z.object({ kind: z.literal("microphone-busy") }), // NotReadableError
  z.object({ kind: z.literal("too-short"), durationMs: z.number() }),
  // The recording exceeded `CaptureLimits.maxBytes`; caught before upload.
  z.object({ kind: z.literal("too-large"), bytes: z.number() }),
  z.object({ kind: z.literal("recorder-failed") }), // MediaRecorder "error" event
]);
export type CaptureError = z.infer<typeof CaptureError>;

// Worker/transport failures, returned in TranscriptionResponse or synthesized
// by the client for network failures.
export const TranscriptionError = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("unauthenticated") }),
  z.object({ kind: z.literal("rate-limited"), retryAt: z.number().int() }),
  // The host's attempt cap for this item is spent; no wait will lift it.
  z.object({ kind: z.literal("attempts-exhausted") }),
  z.object({ kind: z.literal("invalid-request"), field: z.string().max(64) }),
  z.object({
    kind: z.literal("unsupported-audio"),
    sniffed: z.enum(["webm", "mp4", "ogg", "wav", "unknown"]),
  }),
  // The server refused the body as over its byte limit. Distinct from
  // CaptureError's `too-large`: reaching this means the client's and the
  // server's limits disagree, which is a configuration bug, not a long answer.
  z.object({ kind: z.literal("upload-too-large"), maxBytes: z.number() }),
  z.object({ kind: z.literal("no-speech") }), // empty ASR text or provider no-speech signal
  z.object({
    kind: z.literal("provider-unavailable"),
    status: z.number().int().optional(),
  }), // 5xx, timeout, network
  z.object({ kind: z.literal("provider-rejected"), status: z.number().int() }), // 4xx from provider: our bug or config
  z.object({ kind: z.literal("provider-malformed") }), // 2xx with a body that fails the adapter's schema
  z.object({ kind: z.literal("network") }), // client-side only: fetch rejected
]);
export type TranscriptionError = z.infer<typeof TranscriptionError>;

// ---------------------------------------------------------------------------
// Route response (Worker → browser)
// ---------------------------------------------------------------------------

export const TranscriptionResponse = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("ok"),
    responseId: ResponseId,
    transcript: Transcript,
    provenance: Provenance,
  }),
  z.object({ status: z.literal("error"), error: TranscriptionError }),
]);
export type TranscriptionResponse = z.infer<typeof TranscriptionResponse>;

// ---------------------------------------------------------------------------
// Child-facing outcomes
// ---------------------------------------------------------------------------

// Children never see an error string. Every error collapses to one of three
// outcomes; the aide/teacher surface can show the full kind.
export const ChildFacing = z.enum([
  "try-again",
  "ask-a-grown-up",
  "cannot-record-here",
]);
export type ChildFacing = z.infer<typeof ChildFacing>;

export const childFacing = (
  e: CaptureError | TranscriptionError,
): ChildFacing => {
  switch (e.kind) {
    // The device or browser cannot do this at all; retrying changes nothing.
    case "unsupported":
    case "unsupported-audio":
      return "cannot-record-here";
    // Something a child can fix by speaking again.
    case "too-short":
    case "too-large":
    case "recorder-failed":
    case "upload-too-large":
    case "no-speech":
    case "provider-unavailable":
    case "network":
      return "try-again";
    // Needs an adult: a browser setting, a session, a cap, or our own bug.
    case "permission-denied":
    case "no-microphone":
    case "microphone-busy":
    case "unauthenticated":
    case "rate-limited":
    case "attempts-exhausted":
    case "invalid-request":
    case "provider-rejected":
    case "provider-malformed":
      return "ask-a-grown-up";
    default:
      return assertNever(e, "speech error kind");
  }
};

// ---------------------------------------------------------------------------
// Server seam — what the host Worker injects into `createTranscribeRoute`
// ---------------------------------------------------------------------------
//
// Plain types, not schemas: the host constructs these from its own verified
// session and quota store, so nothing here is parsed from untrusted input.

// Who is recording, as the host resolved it from its authenticated session.
// `subject` is the broker subject — the logs' `userId`, and for a classroom
// the aide or teacher, not the child. Only a resolved reader can have an
// `OralResponse` recorded, because the record is keyed by `readerId`.
export type Speaker =
  | {
      readonly tag: "reader";
      readonly subject: string;
      readonly readerId: ReaderId;
    }
  | { readonly tag: "subject-only"; readonly subject: string };

// The host's `authorize` hook: a speaker, or the request is refused as
// `unauthenticated`.
export type AuthorizeOutcome =
  | { readonly tag: "ok"; readonly speaker: Speaker }
  | { readonly tag: "unauthenticated" };

// The host's `spend` hook, checked before any provider call; each refusal maps
// to the `TranscriptionError` of the same kind.
export type QuotaOutcome =
  | { readonly tag: "ok" }
  | { readonly tag: "rate-limited"; readonly retryAt: number }
  | { readonly tag: "attempts-exhausted" };

// ---------------------------------------------------------------------------
// The seam for downstream scoring
// ---------------------------------------------------------------------------

// Everything a scorer (Jev rubric, Claude, a human rater UI) may consume.
export const OralResponse = z.object({
  responseId: ResponseId,
  readerId: ReaderId, // pseudonymous; stays inside our planes
  itemRef: ItemRef,
  promptText: z.string(),
  transcript: Transcript,
  provenance: Provenance,
  capture: CaptureMeta, // durationMs, endedBy — "cut off" matters to a rubric
  attempt: Attempt,
});
export type OralResponse = z.infer<typeof OralResponse>;

// The de-identified projection for any third-party scorer.
export const ScoringInput = OralResponse.pick({
  itemRef: true,
  promptText: true,
  transcript: true,
  capture: true,
});
export type ScoringInput = z.infer<typeof ScoringInput>;

// Builds a fresh object from the four permitted fields rather than copying
// and deleting, so a field added to `OralResponse` later is dropped by
// default instead of forwarded to a vendor by default.
export const toScoringInput = (r: OralResponse): ScoringInput => ({
  itemRef: r.itemRef,
  promptText: r.promptText,
  transcript: r.transcript,
  capture: r.capture,
});
